/**
=========================================================
* Material Dashboard 2 React - Updated Dashboard
=========================================================

* Updated to include dealer management, campaign analysis,
* and sales trends based on user requirements.
*/

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Data
import reportsBarChartData from "layouts/dashboard/data/reportsBarChartData";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";

// Dashboard components
import Projects from "layouts/dashboard/components/Projects";
import OrdersOverview from "layouts/dashboard/components/OrdersOverview";
import useDashboard from "hooks/useDashboard";
import { useEffect, useState } from "react";
import formatRupee from "utils/formatHelper";

const salesTrendOption = [
  {
    label: "Yearly",
    value: "yearly",
  },
  {
    label: "Monthly",
    value: "monthly",
  },
  {
    label: "Weekly",
    value: "weekly",
  },
  {
    label: "Daily",
    value: "daily",
  },
];

const dealersTrendOption = [
  {
    label: "Monthly",
    value: "monthly",
  },
  {
    label: "Weekly",
    value: "weekly",
  },
];

const transformSalesData = (data, dataKey, label) => {
  return {
    labels: data.periods, // Using periods as labels
    datasets: {
      label: label,
      data: data[dataKey], // Using salesData for the dataset
    },
  };
};

function Dashboard() {
  const [salesTrendPeriod, setSalesTrendPeriod] = useState("daily");
  const [dealersPeriod, setDealersPeriod] = useState("weekly");
  const [selectedTerritory, setSelectedTerritory] = useState("");
  const {
    getCardCounts,
    cardCount,
    getSalesTrend,
    salseTrend,
    getDealersOnboardingTrend,
    dealersOnboardingTrend,
    getDealersPerformance,
    dealersPerformance,
  } = useDashboard();

  const handleSalesPeriodChange = (event) => {
    event.preventDefault();
    setSalesTrendPeriod(event.target.value);
    getSalesTrend({ period: event.target.value });
  };

  const handleDealersPeriodchange = (event) => {
    event.preventDefault();
    setDealersPeriod(event.target.value);
    getDealersOnboardingTrend({ period: event.target.value });
  };

  useEffect(() => {
    getCardCounts();
    getSalesTrend({ period: salesTrendPeriod });
    getDealersOnboardingTrend({ period: dealersPeriod });
    getDealersPerformance();
  }, []);
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Statistics Cards */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="store"
                title="Total Sales"
                count={formatRupee(cardCount?.sales?.currentMonth || 0)}
                percentage={{
                  color: "success",
                  amount: `${cardCount?.sales?.percentageChange || 0}%`,
                  label: "compared to last month",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon="groups"
                title="Dealers"
                count={cardCount?.dealers || 0}
                percentage={{
                  color: "info",
                  amount: "",
                  label: "active in the system",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="trending_up"
                title="Campaign ROI"
                count={cardCount?.campaigns || 0}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "active in the system",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="payments"
                title="Payments Due"
                count={formatRupee(cardCount?.paymentOverDue?.totalOverdue || 0)}
                percentage={{
                  color: "error",
                  amount: formatRupee(cardCount?.paymentOverDue?.totalOutstanding || 0),
                  label: "Total out standing",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>

        {/* Analytics and Performance */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            {/* <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="info"
                  title="Dealer Performance"
                  description="Top-performing dealers this month"
                  date="updated 1 day ago"
                  chart={reportsBarChartData}
                />
              </MDBox>
            </Grid> */}
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="success"
                  title="Sales Trends"
                  description={
                    <>
                      (<strong>{salseTrend?.percentageChange}%</strong>) growth in sales.
                    </>
                  }
                  chart={transformSalesData(salseTrend, "salesData", "Sales")}
                  selectedOption={salesTrendPeriod}
                  dataOptions={salesTrendOption}
                  handleChange={handleSalesPeriodChange}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="dark"
                  title="Dealers"
                  description="Dealer Onboarding Trend"
                  chart={transformSalesData(dealersOnboardingTrend, "dealerData", "Dealers")}
                  selectedOption={dealersPeriod}
                  dataOptions={dealersTrendOption}
                  handleChange={handleDealersPeriodchange}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* Detailed Sections */}
        <MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={8}>
              <Projects
                title="Dealer Management"
                description="Manage your dealers and assign territories."
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <OrdersOverview
                title="Campaign Performance"
                description="Insights into recent marketing campaigns."
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default Dashboard;
